import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import ProductTile from "@/components/pages/CycleCounting/ProductTile.vue";
import QuantityChallenge from "@/components/order/QuantityChallenge.vue";
import WavePickTote from "@/components/pages/WavePicking/WavePickTote.vue";
import { bus } from "@/bootstrap/bus";
import { usePickStore } from "@/stores/tasks/pick-tasks";
import extractQrElements from "@/utilities/ScanHandlers";
import { ChallengeTypes, TaskStatus } from "@/stores/tasks/types";
import store from "@/bootstrap/store";
import {
  onBeforeRouteLeave,
  useRoute,
  useRouter,
} from "vue-router/composables";
import { HapticFeedbackEvents } from "@/bootstrap/HapticFeedbackConfig";
import i18n from "@/plugins/i18n";
import NotificationTypes from "@/enums/NotificationTypes";
import {decodeToteScan} from "@/store/wavePicking/helpers";
import AbandonDialog from "@/components/order/AbandonDialog.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'WavePickTotes',
  setup(__props, { expose }) {

const pickStore = usePickStore();
const route = useRoute();
const router = useRouter();

const productScanned = ref<boolean>(false);
const toteScanned = ref<boolean>(false);
const hubooSku = ref<number>(0);
const totePage = ref<number>(1);
const quantityChallengeVisible = ref<boolean>(false);
const abandonmentModalVisible = ref<boolean>(false);
const allowOrderExitValue = ref<boolean>(false);
const totalTotes = ref<number>(0);
const loadingTote = ref<boolean>(false);

bus.$on("barcodeScanned", handleItemScan);

onBeforeUnmount(() => {
  bus.$off("barcodeScanned", handleItemScan);
})

onMounted(() => {
  hubooSku.value = +route.params.sku;
  totalTotes.value = wavePickingTasks?.value?.length;
});

onBeforeRouteLeave((to, _from, next) => {
  const exitRoutes = ["wave_pick_tasks"];
  if (to.name && exitRoutes.includes(to.name)) {
    if (allowOrderExitValue.value) {
      next();
    } else {
      toggleAbandon(true);
      next(false);
    }
  } else {
    next();
  }
});

function handleItemScan(scanResult: string): void {
  const [sku = null, batchId = null] = extractQrElements(scanResult);
  if (sku || batchId) {
    handleSkuScan(Number(sku));
    return;
  }
  if (currentTask.value?.attributes.barcodes.includes(scanResult)) {
    handleSkuScan(hubooSku.value);
    return;
  }
  handleToteScan(scanResult);
}

function handleToteScan(scanResult: string) {
  const label = decodeToteScan(scanResult)
  if(!label || currentTask.value?.attributes.toteName !== label.identifier) {
    challengeIncorrectScan('invalid_tote_scan')
    return;
  }
  toteScanned.value = true;
  if (productScanned.value) {
    setTaskStatus(TaskStatus.COMPLETE);
  }
}

function handleSkuScan(sku: number) {
  if (hubooSku.value !== sku) {
    challengeIncorrectScan("invalid_barcode_scan");
    return;
  }

  if (challengeRequired()) {
    quantityChallengeVisible.value = true;
    return;
  }

  productScanned.value = true;
  if (toteScanned.value) {
    setTaskStatus(TaskStatus.COMPLETE);
  }
}

function challengeIncorrectScan(messageKey: string): void {
  bus.$emit(HapticFeedbackEvents.EVENT_WARNING);
  store.dispatch("core/addNotification", {
    message: i18n.t(messageKey),
    type: NotificationTypes.NOTIFICATION_TYPE_ERROR,
    timeout: 3500,
  });
}

function setTaskStatus(status: TaskStatus) {
  loadingTote.value = true
  setTimeout(() => {
    if (currentTask?.value?.id) {
      pickStore.actions.setPickingTasksQtyPickedByTaskId(
          currentTask.value.id,
          currentTask.value?.attributes.quantity
      );
      pickStore.actions.setPickingTasksStatusByTaskId(
          currentTask.value.id,
          status
      );
    }
    loadingTote.value = false
    if (!currentTask.value) {
      allowOrderExit();
      router.push({ name: "wave_pick_tasks" });
    }
    totePage.value++;
  }, 1000)
  productScanned.value = false;
  toteScanned.value = false;
}

function passQuantityChallenge(): void {
  const taskId = currentTask.value?.id;
  const challengeId = currentTaskQuantityChallenge.value?.id;
  if (taskId && challengeId) {
    pickStore.actions.setChallengeStatusByTaskAndChallengeId(
      taskId,
      challengeId,
      true
    );
  }

  quantityChallengeVisible.value = false;
  productScanned.value = true;
  if (toteScanned.value) {
    setTaskStatus(TaskStatus.COMPLETE);
  }
}

function closeChallenges(): void {
  quantityChallengeVisible.value = false;
}

function challengeRequired(): boolean {
  if (currentTaskQuantityChallenge.value) {
    quantityChallengeVisible.value = true;
    return true;
  }
  return false;
}

function toggleAbandon(abandonmentModalVisibleValue: boolean): void {
  abandonmentModalVisible.value = abandonmentModalVisibleValue;
}

function allowOrderExit(): void {
  allowOrderExitValue.value = true;
}

function goBackToTaskList(): void {
  router.push({ name: "wave_pick_tasks" });
}

//computed
const progress = computed(() => ((totePage.value - 1)/totalTotes.value) * 100)
const taskAttributes = computed(() => wavePickingTasks.value[0].attributes);

const wavePickingTasks = computed(() => {
  return (
    pickStore.getters.getTasks
      .filter((task) => +task.attributes.sku === hubooSku.value)
      .filter((task) => task.status === "new") ?? []
  );
});

const currentTask = computed(() => {
  return wavePickingTasks && wavePickingTasks.value.length > 0
    ? wavePickingTasks.value[0]
    : undefined;
});

const currentTaskQuantityChallenge = computed(() => {
  const taskId = currentTask.value?.id;
  if (!taskId) return null;

  const task = pickStore.getters.getTaskById(taskId);
  if (!task) return null;

  const outstandingQuantityChallenges = task.challenges.filter(
    (challenge) =>
      challenge.type === ChallengeTypes.QUANTITY && challenge.passed === false
  );
  if (outstandingQuantityChallenges.length === 0) {
    return null;
  }
  return outstandingQuantityChallenges[0];
})

// used to expose params and methods in tests
expose({
  handleItemScan,
})


return { __sfc: true,pickStore, route, router, productScanned, toteScanned, hubooSku, totePage, quantityChallengeVisible, abandonmentModalVisible, allowOrderExitValue, totalTotes, loadingTote, handleItemScan, handleToteScan, handleSkuScan, challengeIncorrectScan, setTaskStatus, passQuantityChallenge, closeChallenges, challengeRequired, toggleAbandon, allowOrderExit, goBackToTaskList, progress, taskAttributes, wavePickingTasks, currentTask, currentTaskQuantityChallenge, ProductTile, QuantityChallenge, WavePickTote, AbandonDialog }
}

})